import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import OneOSLoginShell, { ENGINES } from '../components/OneOSLoginShell';

// ---------------------------------------------------------------------------
// Sales Capture login — page-level wrapper around the shared 1OS shell.
//
// This file owns ONLY:
//   * redirect when already authenticated
//   * the credentials → AuthContext.login() call
//   * mapping auth errors to inline error strings
//
// All UI (layout, colors, fonts, motion, copy) lives in
// /components/OneOSLoginShell.jsx. Do not re-implement here.
// ---------------------------------------------------------------------------
const LoginPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  const formatError = (detail) => {
    if (!detail) return 'Authentication failed.';
    if (typeof detail === 'string') return detail;
    if (typeof detail === 'object' && detail.code === 'MODULE_DISABLED') {
      return detail.message || 'Module is disabled.';
    }
    if (Array.isArray(detail)) {
      return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
    }
    return detail.msg || String(detail);
  };

  const handleSubmit = async ({ email, password }) => {
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 503) setError('Authentication service unavailable. Try again shortly.');
      else if (status === 401 || status === 400) setError('Invalid email or password.');
      else setError(formatError(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OneOSLoginShell
      {...ENGINES.sales}
      onSubmit={handleSubmit}
      error={error}
      submitting={submitting}
    />
  );
};

export default LoginPage;
